<template>
  <div class="file-tree-node">
    <v-list-item
      @click="handleItemClick"
      density="compact"
      class="list-item-custom-padding"
    >
      <template #prepend>
        <div class="d-flex align-center">
          <v-icon
            v-if="node.type === 'tree'"
            icon="mdi-chevron-right"
            size="small"
            class="expander-icon"
            :class="{ 'expander-icon--rotated': expanded }"
          ></v-icon>
          <div v-else class="expander-spacer"></div>

          <v-icon
            :icon="node.type === 'tree' ? 'mdi-folder-outline' : 'mdi-file-outline'"
            class="ml-1"
          ></v-icon>
        </div>
      </template>

      <v-list-item-title>{{ node.name }}</v-list-item-title>

      <template #append>
        <v-checkbox-btn
          v-model="node.selected"
          :indeterminate="node.type === 'tree' && node.indeterminate"
          @update:modelValue="handleCheckboxChange"
          @click.stop
          color="primary"
          density="compact"
        ></v-checkbox-btn>
      </template>
    </v-list-item>

    <v-expand-transition>
      <div v-if="node.type === 'tree' && expanded && node.children && node.children.length > 0" class="node-children-indent">
        <file-tree-node
          v-for="child in node.children"
          :key="child.id"
          :node="child"
          @selection-changed="bubbleSelectionChange"
        />
      </div>
    </v-expand-transition>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'

const { node } = defineProps({
  node: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['selection-changed'])

const expanded = ref(node.type === 'tree' ? false : null)

const handleItemClick = () => {
  if (node.type === 'tree') {
    expanded.value = !expanded.value
  } else if (node.type === 'blob') {
    const newValue = !node.selected
    node.selected = newValue
    handleCheckboxChange(newValue)
  }
}

const handleCheckboxChange = (newValue) => {
  if (node.type === 'tree') {
    node.indeterminate = false
    setChildrenSelection(node, newValue)
  }
  emit('selection-changed', { path: node.path, selected: newValue })
}

const setChildrenSelection = (folderNode, isSelected) => {
  if (!folderNode.children) {
    return
  }
  folderNode.children.forEach((child) => {
    child.selected = isSelected
    child.indeterminate = false
    if (child.type === 'tree') {
      setChildrenSelection(child, isSelected)
    }
  })
}

const bubbleSelectionChange = () => {
  if (node.type === 'tree') {
    updateFolderState(node)
  }
  emit('selection-changed', { path: node.path, selected: node.selected, indeterminate: node.indeterminate })
}

const updateFolderState = (folderNode) => {
  if (!folderNode.children || folderNode.children.length === 0) {
    folderNode.indeterminate = false

    return
  }

  const selectedChildrenCount = folderNode.children.filter((c) => c.selected).length
  const indeterminateChildrenCount = folderNode.children.filter((c) => c.indeterminate).length
  const allChildrenSelected = selectedChildrenCount === folderNode.children.length
  const noChildrenSelected = selectedChildrenCount === 0 && indeterminateChildrenCount === 0

  if (noChildrenSelected) {
    folderNode.selected = false
    folderNode.indeterminate = false
  } else if (allChildrenSelected) {
    folderNode.selected = true
    folderNode.indeterminate = false
  } else {
    folderNode.selected = false
    folderNode.indeterminate = true
  }
}

watch(() => node.children, (newChildren) => {
  if (node.type === 'tree' && newChildren) {
    updateFolderState(node)
  }
}, { deep: true })

onMounted(() => {
  if (node.type === 'tree') {
    updateFolderState(node)
  }
})

</script>

<style scoped>
.file-tree-node .v-list-item {
  min-height: 36px;
}

.file-tree-node .v-list-item-title {
  font-size: 0.9rem;
  margin-left: 4px;
}

.node-children-indent {
  padding-left: 20px;
}

.expander-icon {
  transition: transform 0.2s ease-in-out;
  opacity: 0.6;
}

.expander-icon--rotated {
  transform: rotate(90deg);
}

.expander-spacer {
  min-width: 20px;
}
</style>
